import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
  Link,
  useTheme
} from '@mui/material';
import {
  Lock as LockIcon,
  SwapHoriz as SwapIcon,
  BookmarkAdd as SaveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import betTrackerService from '../../services/betTrackerService';
import BackButton from '../BackButton';
import { slipToBetRecord } from '../../services/betTracker/records';
import {
  toggleLeg,
  isCandidateSelected,
  selectedLegForGame,
  customSlipMetrics,
  canSaveCustomSlip,
  filterGamesByQuery,
  MAX_CUSTOM_LEGS
} from '../../utils/parlayCustom';
import { buildRoundRobin } from '../../utils/roundRobin';
import { findExposureForTeams } from '../../services/betTracker/exposure';

/**
 * Parlay Builder (Phase 1: game lines) — spec: Andrew, 2026-10-31.
 *
 * The parlay-builder Netlify function builds the slip server-side from
 * the current odds snapshot (one reference book, one leg per game) and
 * returns every game's candidate list, so swaps and stake changes
 * recalculate instantly on the client with no further fetch:
 *   combined odds = product of leg decimals
 *   true chance   = product of leg fair (de-vigged) probabilities
 *   EV            = true chance x combined decimal - 1
 *
 * Premium-only (Yianni, 2026-10-31 — supersedes the spec section 6
 * free/premium split): guests and free accounts get the premium gate
 * view below and never reach the builder; Premium gets the full
 * builder — Safest AND Best Value, 2-7 legs, leg swapping. The gate
 * follows the site's existing premium pattern: useSubscription()
 * (SubscriptionContext derives isPremium from the user record's
 * isPremium + status + tier fields) plus a lock/upsell card routing
 * to /subscription, as in FeaturedPicks. The parlay-builder function
 * itself just builds what's asked; the gate lives here at page level.
 *
 * The card's action is "Save to Bet Tracker": with no sportsbook
 * affiliate agreements in place there is no outbound bet button
 * (Apple Guideline 5.3 would also demand proof of authorization for
 * book-specific bet links). Saving writes the displayed slip + stake
 * to the user's Bet Tracker (users/{uid}/bets) via betTrackerService.
 *
 * Two modes (tabs, Yianni 2026-10-31): "BettorAI Suggested" is the
 * auto-builder described above. "Build Your Own" lets the user
 * compose a slip from the same payload's candidate board — every
 * game, every candidate leg — under the same rules (one leg per
 * game, 2-7 legs to save). Its selection state and slip math live in
 * src/utils/parlayCustom.js with formulas identical to the engine's,
 * so both modes price the same legs the same way, and both save
 * through the same slipToBetRecord + addBet flow.
 *
 * Winning Features addendum (2026-11-02): the function now also
 * returns per-leg cross-book `pricing` and per-candidate `tags`.
 * Below the Suggested slip card: a Best Price panel (each leg's best
 * price and book; best single-book payout for everyone who reaches
 * the builder, split-across-books payout as the Premium tier), a
 * Round Robin converter (Premium; saves one tracker record per
 * combination, grouped by a shared groupId), leg tag chips (NFL key
 * numbers / Wong teaser windows, NBA back-to-backs), and an Exposure
 * Guard warning — checked before either tab's save lands — when a
 * saved slip's teams already sit on pending tracked tickets. The
 * page-level Premium gate above still governs access — the in-panel
 * Premium checks are the addendum's tiering, in place if the page
 * gate is ever relaxed.
 *
 * Phase 2 (player props, 2026-11-04): Build Your Own gains a
 * "Game Line | Player Props" selector next to the book selector.
 * It switches ONLY the board — the hand-built slip (customLegs) is
 * one state across both views, so a slip can mix game legs and
 * prop legs added from either side, under the same one-leg-per-
 * game and one-book rules. Prop boards load per game from the
 * event-props function (which rides the odds proxy's shared cache)
 * at the currently resolved book; each prop offering shows the
 * player's L5/L10 hit rates for that exact line from the
 * player-hit-rate function when the stats store has him — no data,
 * no chips, never a placeholder. BettorAI Suggested stays
 * game-leg-only in this phase.
 */

const SPORT_OPTIONS = [
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'icehockey_nhl', label: 'NHL' }
];

// Board groupings for Build Your Own, in display order (the payload
// only ever carries these three game-line markets).
const MARKET_GROUPS = [
  { key: 'h2h', label: 'Moneyline' },
  { key: 'spreads', label: 'Spread' },
  { key: 'totals', label: 'Total' }
];

const STAKE_PRESETS = [10, 25, 100];

// Spec section 8 — exact footer text, do not edit.
const DISCLAIMER =
  '21+. For information and entertainment only. Not a sportsbook. No pick is guaranteed. Bet only where legal. Gambling problem? Call 1-800-GAMBLER (NY: 877-8-HOPENY).';

const functionUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions/parlay-builder'
    : '/.netlify/functions/parlay-builder';

const eventPropsUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions/event-props'
    : '/.netlify/functions/event-props';

const hitRateUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions/player-hit-rate'
    : '/.netlify/functions/player-hit-rate';

// Sports with a Phase 2 props board (mirrors the event-props
// function's PROP_MARKETS — NCAAF props are too thin at the books).
const PROPS_SPORTS = new Set(['americanfootball_nfl', 'basketball_nba', 'icehockey_nhl']);

// One hit-rate lookup per (player, market, line): both sides read
// the same windows (overPct / underPct), so the key omits side.
const hitRateKey = (row) => `${row.player}|${row.hitRateMarket}|${row.line}`;

const formatAmerican = (american) => (american > 0 ? `+${american}` : `${american}`);

const decimalToAmerican = (decimal) => {
  if (decimal >= 2) return Math.round((decimal - 1) * 100);
  return Math.round(-100 / (decimal - 1));
};

const formatPct = (fraction, digits = 1) => `${(fraction * 100).toFixed(digits)}%`;

const formatSignedPct = (fraction) => {
  const pct = fraction * 100;
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
};

const formatMoney = (value) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatStartEt = (iso) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).formatToParts(new Date(iso));
    const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
    return `${get('weekday')}, ${get('month')} ${get('day')} · ${get('hour')}:${get('minute')} ${get('dayPeriod')} ET`;
  } catch (e) {
    return '';
  }
};

const sameCandidate = (a, b) =>
  a && b && a.eventId === b.eventId && a.market === b.market &&
  a.selection === b.selection && a.american === b.american;

// Identity of a priced leg: the exact bet (event, market, outcome,
// point) the function's cross-book pricing was computed for.
const legKey = (leg) =>
  `${leg.eventId}|${leg.market}|${leg.name}|${leg.point}`;

/** One-shot read of the user's tracked bets via the subscribe API. */
const fetchTrackedBetsOnce = (uid) =>
  new Promise((resolve) => {
    let done = false;
    const finish = (bets) => {
      if (!done) {
        done = true;
        resolve(bets);
      }
    };
    const unsubscribe = betTrackerService.subscribeBets(
      uid,
      (records) => {
        finish(records);
        if (typeof unsubscribe === 'function') unsubscribe();
      },
      () => finish([])
    );
  });

const ParlayBuilderScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isPremium, loading: subscriptionLoading } = useSubscription();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();

  // Odds/payout values are accent-orange (#F97316) in light mode.
  // In dark mode that same orange is the button fill (theme button
  // rule), so orange odds text would sit on an identically orange
  // background and vanish — dark mode renders them white instead.
  const oddsTextColor = theme.palette.mode === 'dark' ? '#FFFFFF' : '#F97316';

  // Text printed ON a leg button — the selection/team label and
  // its odds — sits on the theme's filled button background
  // (orange in dark mode, blue in light mode), so, like every
  // other text on a filled control, it is white in BOTH modes.
  // (Slip-card odds above use oddsTextColor instead: that card
  // is dark in both modes and is not a filled control.)
  const buttonOddsColor = '#FFFFFF';

  const initialSport = SPORT_OPTIONS.some((s) => s.key === searchParams.get('sport'))
    ? searchParams.get('sport')
    : 'americanfootball_nfl';

  const [sport, setSport] = useState(initialSport);
  const [legsCount, setLegsCount] = useState(3);
  const [mode, setMode] = useState('safest');
  const [stake, setStake] = useState('25');
  const [data, setData] = useState(null);
  const [currentLegs, setCurrentLegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedFingerprint, setSavedFingerprint] = useState(null);
  const [exposureNote, setExposureNote] = useState(null);

  // Round Robin converter state.
  const [rrSizes, setRrSizes] = useState([2]);
  const [rrUnitStake, setRrUnitStake] = useState('10');
  const [rrSaving, setRrSaving] = useState(false);
  const [rrSavedKey, setRrSavedKey] = useState(null);

  // Build Your Own state: the user's own selection from the board
  // (full candidate objects from the payload), its own stake, and
  // the same saved-fingerprint discipline as the suggested slip.
  const [builderTab, setBuilderTab] = useState('suggested');
  const [customLegs, setCustomLegs] = useState([]);
  const [customStake, setCustomStake] = useState('25');
  const [customSaving, setCustomSaving] = useState(false);
  const [customSavedFingerprint, setCustomSavedFingerprint] = useState(null);
  const [customQuery, setCustomQuery] = useState('');
  const [customExposureNote, setCustomExposureNote] = useState(null);

  // Build Your Own book filter: null = Auto (the function's reference
  // book). An explicit book is sent as &book=<key> on every fetch and
  // persists across sport changes; a slip can only ever carry one
  // book's odds, so changing it clears the hand-built selection.
  const [bookKey, setBookKey] = useState(null);

  // Phase 2 props state. legView is ONLY a board switch — it never
  // touches customLegs (see the header note). Props load lazily,
  // one game at a time, into propsByEvent[eventId] =
  // { status: 'loading' | 'ready' | 'error', payload?, message? }.
  // hitRates maps hitRateKey(row) -> windows (or null when the
  // stats store has nothing for that player/line).
  const [legView, setLegView] = useState('game');
  const [propsByEvent, setPropsByEvent] = useState({});
  const [expandedProps, setExpandedProps] = useState({});
  const [hitRates, setHitRates] = useState({});

  // Premium-only: anyone who isn't Premium (guest or free account)
  // gets the gate view instead of the builder.
  const gated = !subscriptionLoading && !isPremium;
  const gateMessage = currentUser
    ? 'The Parlay Builder is a Premium feature — build de-vigged parlays in Safest or Best Value mode, swap legs, and save slips straight to your Bet Tracker.'
    : 'The Parlay Builder is a Premium feature — build de-vigged parlays in Safest or Best Value mode, swap legs, and save slips straight to your Bet Tracker. Sign in to subscribe, or see what Premium includes.';

  useEffect(() => {
    if (gated || subscriptionLoading) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const bookParam = bookKey ? `&book=${encodeURIComponent(bookKey)}` : '';
    const url = `${functionUrl()}?sport=${encodeURIComponent(sport)}&legs=${legsCount}&mode=${mode}${bookParam}`;
    fetch(url, { signal: controller.signal })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error((payload && payload.error) || `Builder failed (${response.status})`);
        }
        return payload;
      })
      .then((payload) => {
        setData(payload);
        setCurrentLegs(payload && payload.slip ? payload.slip.legs : []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setData(null);
        setCurrentLegs([]);
        setError(err.message || 'Could not build a parlay right now.');
        setLoading(false);
      });

    return () => controller.abort();
  }, [sport, legsCount, mode, bookKey, gated, subscriptionLoading, reloadKey]);

  const metrics = useMemo(() => {
    let combinedDecimal = 1;
    let trueChance = 1;
    for (const leg of currentLegs) {
      combinedDecimal *= leg.decimal;
      trueChance *= leg.fairProb;
    }
    return {
      combinedDecimal,
      combinedAmerican: currentLegs.length ? decimalToAmerican(combinedDecimal) : 0,
      trueChance,
      ev: trueChance * combinedDecimal - 1
    };
  }, [currentLegs]);

  const stakeAmount = parseFloat(stake) || 0;
  const payout = stakeAmount * metrics.combinedDecimal;

  // Cross-book pricing for the slip as built (Winning Features
  // addendum). Keyed by exact bet so leg rows can label best prices;
  // after a swap, the swapped leg simply has no pricing entry.
  const pricing = data && data.pricing ? data.pricing : null;
  const pricingByLegKey = useMemo(() => {
    const map = new Map();
    if (pricing && Array.isArray(pricing.legs)) {
      for (const pricedLeg of pricing.legs) {
        map.set(legKey(pricedLeg), pricedLeg);
      }
    }
    return map;
  }, [pricing]);
  const slipChanged = useMemo(() => {
    if (!data || !data.slip) return false;
    const builtKeys = new Set(data.slip.legs.map(legKey));
    return currentLegs.some((leg) => !builtKeys.has(legKey(leg)));
  }, [data, currentLegs]);

  // Round Robin over the currently displayed legs.
  const rrUnitAmount = parseFloat(rrUnitStake) || 0;
  const roundRobin = useMemo(
    () => buildRoundRobin(currentLegs, rrSizes, rrUnitAmount),
    [currentLegs, rrSizes, rrUnitAmount]
  );
  const rrCountsBySize = useMemo(() => {
    const counts = {};
    for (const combo of roundRobin.combos) {
      counts[combo.size] = (counts[combo.size] || 0) + 1;
    }
    return counts;
  }, [roundRobin]);
  const rrFingerprint = useMemo(
    () => JSON.stringify({
      legs: currentLegs.map((leg) => [leg.eventId, leg.market, leg.selection, leg.american]),
      sizes: rrSizes,
      unit: rrUnitAmount
    }),
    [currentLegs, rrSizes, rrUnitAmount]
  );
  const rrSaved = rrSavedKey !== null && rrSavedKey === rrFingerprint;

  const handleSwap = (index) => {
    if (!data) return;
    const leg = currentLegs[index];
    const game = (data.games || []).find((g) => g.eventId === leg.eventId);
    if (!game || game.candidates.length < 2) return;
    const currentIdx = game.candidates.findIndex((c) => sameCandidate(c, leg));
    const next = game.candidates[(currentIdx + 1) % game.candidates.length];
    setCurrentLegs((prev) => prev.map((l, i) => (i === index ? next : l)));
  };

  const book = data && data.book ? data.book : null;
  const sportTitle = data ? data.sportTitle : (SPORT_OPTIONS.find((s) => s.key === sport) || {}).label;

  // Switching sports invalidates a hand-built selection — its legs
  // came from the previous sport's board.
  const handleSportChange = (value) => {
    if (!value || value === sport) return;
    setSport(value);
    setCustomLegs([]);
    setCustomSavedFingerprint(null);
    setPropsByEvent({});
    setExpandedProps({});
    setHitRates({});
    if (!PROPS_SPORTS.has(value)) setLegView('game');
  };

  // Switching books reprices the whole board at another book, so a
  // hand-built selection priced at the old book is cleared too —
  // but the book choice itself persists across sport changes.
  const handleBookChange = (key) => {
    if (key === bookKey) return;
    setBookKey(key);
    setCustomLegs([]);
    setCustomSavedFingerprint(null);
    setPropsByEvent({});
    setHitRates({});
  };

  // "Saved" applies to one exact slip + stake: any swap or stake edit
  // changes the fingerprint and re-arms the Save button.
  const slipFingerprint = useMemo(
    () => JSON.stringify({
      legs: currentLegs.map((leg) => [leg.eventId, leg.market, leg.selection, leg.american]),
      stake: stakeAmount
    }),
    [currentLegs, stakeAmount]
  );
  const isSaved = savedFingerprint !== null && savedFingerprint === slipFingerprint;

  // The exposure warning belongs to one exact saved slip; any change
  // to the slip or stake clears it.
  useEffect(() => {
    setExposureNote(null);
  }, [slipFingerprint]);

  // Exposure Guard (Winning Features addendum): check a slip's teams
  // against the user's existing PENDING tracked tickets before the
  // save lands, so the new ticket never counts itself. Failure-
  // isolated — a failed read just means no warning. Returns the
  // formatted warning for the slip, or null when nothing overlaps.
  // One helper, shared by both save paths (Suggested + Build Your
  // Own), since both end at slipToBetRecord + addBet.
  const exposureWarningFor = async (legsToCheck) => {
    try {
      const existingBets = await fetchTrackedBetsOnce(currentUser.uid);
      const slipTeams = [
        ...new Set(
          legsToCheck.flatMap((leg) => [leg.homeTeam, leg.awayTeam]).filter(Boolean)
        )
      ];
      const overlaps = findExposureForTeams(existingBets, slipTeams);
      return overlaps.length > 0
        ? overlaps
            .map(
              (o) =>
                `${o.team} — already on ${o.count} of your pending ticket${o.count === 1 ? '' : 's'}`
            )
            .join(' · ')
        : null;
    } catch (exposureError) {
      return null;
    }
  };

  const handleSaveToTracker = async () => {
    if (!currentUser) {
      // Defensive: the premium gate already keeps guests off the
      // builder, but if auth state flips mid-view, route through the
      // PR #19 login-return pattern back to this exact builder view.
      navigate('/login', { state: { from: `/parlay-builder?sport=${sport}` } });
      return;
    }
    if (isSaved || saving || stakeAmount <= 0 || currentLegs.length === 0) return;
    setSaving(true);
    try {
      // Exposure Guard: check this slip's teams against the user's
      // existing PENDING tickets before the save lands, so the new
      // ticket never counts itself.
      const exposureNoteForSave = await exposureWarningFor(currentLegs);
      const record = slipToBetRecord({
        legs: currentLegs,
        sport,
        stake: stakeAmount,
        bookTitle: book ? book.title : null
      });
      await betTrackerService.addBet(currentUser.uid, record);
      setSavedFingerprint(slipFingerprint);
      setExposureNote(exposureNoteForSave);
      showToast('Saved to Bet Tracker', 'success');
    } catch (saveError) {
      showToast('Could not save this parlay — please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRoundRobin = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/parlay-builder?sport=${sport}` } });
      return;
    }
    if (rrSaving || rrSaved || rrUnitAmount <= 0 || roundRobin.combos.length === 0) return;
    setRrSaving(true);
    try {
      // One tracker record per combination, all sharing a groupId so
      // the group can be recognized as a single round robin.
      const groupId = `rr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      for (const combo of roundRobin.combos) {
        const record = slipToBetRecord({
          legs: combo.legs,
          sport,
          stake: rrUnitAmount,
          bookTitle: book ? book.title : null
        });
        record.groupId = groupId;
        record.roundRobin = { size: combo.size, groupSize: roundRobin.combos.length };
        await betTrackerService.addBet(currentUser.uid, record);
      }
      setRrSavedKey(rrFingerprint);
      showToast(
        `Saved ${roundRobin.combos.length} round robin parlay${roundRobin.combos.length === 1 ? '' : 's'} to Bet Tracker`,
        'success'
      );
    } catch (saveError) {
      showToast(
        'Could not save the whole round robin — check your Bet Tracker before trying again.',
        'error'
      );
    } finally {
      setRrSaving(false);
    }
  };

  // ---- Build Your Own ---------------------------------------------
  // Same payload, same card math (src/utils/parlayCustom.js mirrors
  // the engine's formulas), same save flow — only the selection is
  // the user's instead of the auto-builder's.
  const visibleCustomGames = useMemo(
    () => filterGamesByQuery(data && data.games ? data.games : [], customQuery),
    [data, customQuery]
  );
  const customMetrics = useMemo(() => customSlipMetrics(customLegs), [customLegs]);
  const customStakeAmount = parseFloat(customStake) || 0;
  const customPayout = customStakeAmount * customMetrics.combinedDecimal;
  const customCanSave = canSaveCustomSlip(customLegs);

  const customFingerprint = useMemo(
    () => JSON.stringify({
      legs: customLegs.map((leg) => [leg.eventId, leg.market, leg.selection, leg.american]),
      stake: customStakeAmount
    }),
    [customLegs, customStakeAmount]
  );
  const customIsSaved = customSavedFingerprint !== null && customSavedFingerprint === customFingerprint;

  // Same discipline as the suggested slip: the custom exposure
  // warning belongs to one exact saved slip.
  useEffect(() => {
    setCustomExposureNote(null);
  }, [customFingerprint]);

  const handleToggleCandidate = (candidate) => {
    const next = toggleLeg(customLegs, candidate);
    if (next === customLegs) {
      // toggleLeg returns the same array only when the slip is full
      // and the tap was on a new game.
      showToast(`A parlay can have at most ${MAX_CUSTOM_LEGS} legs — remove one first.`, 'info');
      return;
    }
    setCustomLegs(next);
  };

  // ---- Player props (Phase 2) ------------------------------------
  // Boards load lazily per game from event-props at the resolved
  // book (the explicit filter if set, else the game board's book),
  // so the one-book rule holds across both views. Hit rates follow
  // in ONE batch call per game (player-hit-rate POST): unique
  // (player, market, line) offerings only; a 404 per item just
  // means no chips for that offering.

  const fetchHitRatesForRows = async (rows) => {
    const unique = new Map();
    for (const row of rows || []) {
      if (!row.hitRateMarket || typeof row.line !== 'number') continue;
      const key = hitRateKey(row);
      if (!unique.has(key)) {
        unique.set(key, {
          player: row.player,
          league: row.league,
          market: row.hitRateMarket,
          line: row.line
        });
      }
    }
    const items = [...unique.values()];
    if (items.length === 0) return;
    try {
      const response = await fetch(hitRateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) return;
      const payload = await response.json();
      const found = {};
      for (const result of (payload && payload.results) || []) {
        const item = items[result.index];
        if (!item) continue;
        const key = `${item.player}|${item.market}|${item.line}`;
        found[key] = result.status === 200 && result.data ? result.data.windows : null;
      }
      setHitRates((prev) => ({ ...prev, ...found }));
    } catch (hitRateError) {
      // Research layer is best-effort: no chips, never a broken board.
    }
  };

  const loadPropsForGame = async (game) => {
    const existing = propsByEvent[game.eventId];
    if (existing && (existing.status === 'loading' || existing.status === 'ready')) return;
    setPropsByEvent((prev) => ({ ...prev, [game.eventId]: { status: 'loading' } }));
    try {
      const resolvedBook = bookKey || (data && data.book ? data.book.key : null);
      const bookParam = resolvedBook ? `&book=${encodeURIComponent(resolvedBook)}` : '';
      const url = `${eventPropsUrl()}?sport=${encodeURIComponent(sport)}&eventId=${encodeURIComponent(game.eventId)}${bookParam}`;
      const response = await fetch(url);
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error((payload && payload.error) || `Props failed (${response.status})`);
      }
      setPropsByEvent((prev) => ({ ...prev, [game.eventId]: { status: 'ready', payload } }));
      fetchHitRatesForRows(payload ? payload.rows : []);
    } catch (propsError) {
      setPropsByEvent((prev) => ({
        ...prev,
        [game.eventId]: { status: 'error', message: propsError.message || 'Could not load props.' }
      }));
    }
  };

  const handleTogglePropsExpanded = (game) => {
    const isOpen = Boolean(expandedProps[game.eventId]);
    setExpandedProps((prev) => ({ ...prev, [game.eventId]: !isOpen }));
    if (!isOpen) loadPropsForGame(game);
  };

  // In props view the team search also matches players — but only
  // for games whose props have actually loaded (we never guess at
  // a roster we haven't fetched).
  const visiblePropsGames = useMemo(() => {
    const q = customQuery.trim().toLowerCase();
    if (!q) return visibleCustomGames;
    return (data && data.games ? data.games : []).filter((game) => {
      const teamHit = [game.homeTeam, game.awayTeam, game.matchup].some(
        (field) => typeof field === 'string' && field.toLowerCase().includes(q)
      );
      if (teamHit) return true;
      const entry = propsByEvent[game.eventId];
      if (!entry || entry.status !== 'ready' || !entry.payload) return false;
      return (entry.payload.rows || []).some((row) => row.player.toLowerCase().includes(q));
    });
  }, [data, customQuery, visibleCustomGames, propsByEvent]);

  // One offering = one player + market + line, with its side
  // row(s) as the tap targets — the same full-width price-button
  // idiom as the game board. Hit-rate windows render only when the
  // batch lookup returned data for that exact line (never a
  // placeholder), as research context — NOT "true chance"; the
  // true-chance figure on a prop button is the de-vigged fairProb,
  // exactly like a game leg.
  const renderPropOffering = (sides) => {
    const first = sides[0];
    const windows = first.hitRateMarket && typeof first.line === 'number'
      ? hitRates[hitRateKey(first)]
      : null;
    const sideButton = (row) => {
      const selected = isCandidateSelected(customLegs, row);
      return (
        <Button
          key={row.side}
          fullWidth
          variant={selected ? 'contained' : 'outlined'}
          onClick={() => handleToggleCandidate(row)}
          aria-pressed={selected}
          sx={{
            justifyContent: 'space-between',
            textTransform: 'none',
            py: 1,
            ...(selected
              ? { bgcolor: '#14532D', '&:hover': { bgcolor: '#14532D' } }
              : { color: '#FFFFFF', borderColor: 'rgba(0,0,0,0.25)' })
          }}
        >
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700 }}>
            {selected && <CheckIcon fontSize="small" />}
            {row.market === 'player_anytime_td' ? 'Anytime TD' : row.name}
          </Box>
          <Box component="span" sx={{ textAlign: 'right' }}>
            <Box component="span" sx={{ fontWeight: 800, color: buttonOddsColor }}>
              {formatAmerican(row.american)}
            </Box>
            <Box component="span" sx={{ display: 'block', fontSize: '0.7rem', opacity: 0.75 }}>
              {formatPct(row.fairProb)} true{row.fairProbEstimated ? ' (est.)' : ''}
            </Box>
          </Box>
        </Button>
      );
    };
    return (
      <Box key={`${first.player}-${first.market}-${first.line}`} sx={{ mt: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {first.player}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 600 }}>
            {first.marketLabel}
            {typeof first.line === 'number' ? ` ${first.line}` : ''}
          </Typography>
        </Typography>
        {first.market === 'player_anytime_td' ? (
          <Box sx={{ mt: 0.75 }}>{sideButton(first)}</Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
            {sides.map((row) => (
              <Box key={row.side} sx={{ flex: 1 }}>
                {sideButton(row)}
              </Box>
            ))}
          </Box>
        )}
        {windows && (windows.L5 || windows.L10) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Hit rate at this line —
            {windows.L5 ? ` L5: Over ${formatPct(windows.L5.overPct)} · Under ${formatPct(windows.L5.underPct)} (${windows.L5.played} gms)` : ''}
            {windows.L10 ? ` · L10: Over ${formatPct(windows.L10.overPct)} · Under ${formatPct(windows.L10.underPct)} (${windows.L10.played} gms)` : ''}
          </Typography>
        )}
      </Box>
    );
  };

  // The props board: same game cards as the game board, each with a
  // Player Props expansion that loads on first open.
  const renderPropsBoard = () => {
    if (visiblePropsGames.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No games match &apos;{customQuery}&apos;
            </Typography>
          </CardContent>
        </Card>
      );
    }
    return (
      <>
        {visiblePropsGames.map((game) => {
          const gameLeg = selectedLegForGame(customLegs, game.eventId);
          const entry = propsByEvent[game.eventId];
          const isOpen = Boolean(expandedProps[game.eventId]);
          const rows = entry && entry.status === 'ready' && entry.payload ? entry.payload.rows : [];
          const offerings = new Map();
          for (const row of rows) {
            const key = `${row.player}|${row.market}|${row.line}`;
            if (!offerings.has(key)) offerings.set(key, []);
            offerings.get(key).push(row);
          }
          return (
            <Card key={game.eventId}>
              <CardContent>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {game.matchup}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatStartEt(game.commenceTime)}
                  </Typography>
                  {gameLeg && (
                    <Chip
                      label={`On your slip: ${gameLeg.selection}`}
                      size="small"
                      sx={{ bgcolor: '#14532D', color: '#FFFFFF', fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleTogglePropsExpanded(game)}
                  aria-expanded={isOpen}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                >
                  {isOpen ? 'Hide player props' : 'Player props'}
                </Button>
                {isOpen && (
                  <Box sx={{ mt: 0.5 }}>
                    {(!entry || entry.status === 'loading') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Loading props…
                        </Typography>
                      </Box>
                    )}
                    {entry && entry.status === 'error' && (
                      <Box sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="error">
                          {entry.message}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => loadPropsForGame(game)}
                          sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                        >
                          Retry
                        </Button>
                      </Box>
                    )}
                    {entry && entry.status === 'ready' && rows.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                        No player props at {entry.payload.book ? entry.payload.book.title : 'this book'} for this game yet.
                      </Typography>
                    )}
                    {entry && entry.status === 'ready' && entry.payload.bookFallback && entry.payload.book && rows.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        That book has no props for this game — showing {entry.payload.book.title} instead.
                      </Typography>
                    )}
                    {[...offerings.values()].map((sides) => renderPropOffering(sides))}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </>
    );
  };

  const handleSaveCustomToTracker = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/parlay-builder?sport=${sport}` } });
      return;
    }
    if (customIsSaved || customSaving || customStakeAmount <= 0 || !customCanSave) return;
    setCustomSaving(true);
    try {
      // Exposure Guard: same pre-save check as the suggested slip.
      const exposureNoteForSave = await exposureWarningFor(customLegs);
      const record = slipToBetRecord({
        legs: customLegs,
        sport,
        stake: customStakeAmount,
        bookTitle: book ? book.title : null
      });
      await betTrackerService.addBet(currentUser.uid, record);
      setCustomSavedFingerprint(customFingerprint);
      setCustomExposureNote(exposureNoteForSave);
      showToast('Saved to Bet Tracker', 'success');
    } catch (saveError) {
      showToast('Could not save this parlay — please try again.', 'error');
    } finally {
      setCustomSaving(false);
    }
  };

  return (
    <Box>
      <BackButton />
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
        Parlay Builder
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Two ways to build: take the BettorAI Suggested slip, or build your own from the board.
        Either way we show the true chance the parlay hits — with the bookmaker&apos;s margin
        stripped out.
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 3 }}>
        <Link component={RouterLink} to="/parlay-record" underline="hover">
          Parlay record
        </Link>
        {' — every suggested slip we archive each morning, graded and on the record.'}
      </Typography>

      <Tabs
        value={builderTab}
        onChange={(_, value) => setBuilderTab(value)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="suggested" label="BettorAI Suggested" sx={{ textTransform: 'none', fontWeight: 700 }} />
        <Tab value="custom" label="Build Your Own" sx={{ textTransform: 'none', fontWeight: 700 }} />
      </Tabs>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                Sport
              </Typography>
              <ToggleButtonGroup
                value={sport}
                exclusive
                onChange={(_, value) => handleSportChange(value)}
                size="small"
                sx={{ flexWrap: 'wrap' }}
              >
                {SPORT_OPTIONS.map((option) => (
                  <ToggleButton key={option.key} value={option.key} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {builderTab === 'suggested' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Legs
                </Typography>
                <ToggleButtonGroup
                  value={legsCount}
                  exclusive
                  onChange={(_, value) => value && setLegsCount(value)}
                  size="small"
                >
                  {[2, 3, 4, 5, 6, 7].map((count) => (
                    <ToggleButton key={count} value={count} sx={{ fontWeight: 600 }}>
                      {count}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Mode
                </Typography>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, value) => value && setMode(value)}
                  size="small"
                >
                  <ToggleButton value="safest" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Safest
                  </ToggleButton>
                  <ToggleButton value="value" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Best Value
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            )}

            {builderTab === 'custom' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Markets
                </Typography>
                <ToggleButtonGroup
                  value={legView}
                  exclusive
                  onChange={(_, value) => value && setLegView(value)}
                  size="small"
                >
                  <ToggleButton value="game" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Game Line
                  </ToggleButton>
                  <ToggleButton
                    value="props"
                    disabled={!PROPS_SPORTS.has(sport)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Player Props
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {PROPS_SPORTS.has(sport)
                    ? 'Switching views never touches your slip — mix game legs and prop legs freely.'
                    : 'Player props are not offered for this sport.'}
                </Typography>
              </Box>
            </Box>
            )}

            {builderTab === 'custom' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                Odds from
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={book ? `Auto (${book.title})` : 'Auto'}
                  clickable
                  onClick={() => handleBookChange(null)}
                  color={bookKey === null ? 'primary' : 'default'}
                  variant={bookKey === null ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
                {((data && data.books) || []).map((option) => (
                  <Chip
                    key={option.key}
                    label={option.title}
                    clickable
                    onClick={() => handleBookChange(option.key)}
                    color={bookKey === option.key ? 'primary' : 'default'}
                    variant={bookKey === option.key ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                A slip can only use one book&apos;s odds — switching books clears your picks.
              </Typography>
              {data && data.bookFallback && book && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  That book has no lines for this slate — showing {book.title} instead.
                </Typography>
              )}
            </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Premium gate (mirrors the Premium Pick lock pattern) */}
      {gated && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Premium Feature
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
              {gateMessage}
            </Typography>
            {currentUser ? (
              <Button variant="contained" onClick={() => navigate('/subscription')}>
                Upgrade to Premium
              </Button>
            ) : (
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate('/login', { state: { from: `/parlay-builder?sport=${sport}` } })}
                >
                  Sign In
                </Button>
                <Button variant="outlined" onClick={() => navigate('/subscription')}>
                  See Premium
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {!gated && (loading || subscriptionLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!gated && !loading && !subscriptionLoading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!gated && !loading && !subscriptionLoading && !error && data && data.insufficient && !data.slip && builderTab === 'suggested' && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Not enough games on the board
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.maxLegs === 0
                ? `No upcoming ${sportTitle} games with odds right now — check back closer to game day.`
                : `Only ${data.maxLegs} upcoming ${sportTitle} game has odds right now, and a parlay needs at least 2 legs. Try another sport.`}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!gated && !loading && !subscriptionLoading && !error && data && data.slip && currentLegs.length > 0 && builderTab === 'suggested' && (
        <>
          {data.insufficient && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only {data.maxLegs} games available for {sportTitle} right now — showing a {data.maxLegs}-leg card.
            </Alert>
          )}

          {/* The parlay card (spec section 5 palette) */}
          <Card sx={{ bgcolor: '#0A1F18', color: '#FFFFFF', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip
                  label={mode === 'value' ? 'BEST VALUE' : 'SAFEST'}
                  size="small"
                  sx={{ bgcolor: '#F97316', color: '#FFFFFF', fontWeight: 700 }}
                />
                {book && (
                  <Chip
                    label={`All legs at ${book.title}`}
                    size="small"
                    variant="outlined"
                    sx={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.4)' }}
                  />
                )}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', ml: 'auto' }}>
                  Odds as of {data.oddsAsOf}
                </Typography>
              </Box>

              <Stack divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />}>
                {currentLegs.map((leg, index) => (
                  <Box
                    key={`${leg.eventId}-${leg.market}-${leg.selection}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
                        {leg.selection}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        {leg.matchup} · {formatStartEt(leg.commenceTime)}
                      </Typography>
                      {isPremium && Array.isArray(leg.tags) && leg.tags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {leg.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                color: '#FDBA74',
                                borderColor: 'rgba(249,115,22,0.45)',
                                bgcolor: 'rgba(249,115,22,0.15)'
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: oddsTextColor }}>
                        {formatAmerican(leg.american)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        {formatPct(leg.fairProb)} true
                      </Typography>
                      {(() => {
                        const pricedLeg = pricingByLegKey.get(legKey(leg));
                        return pricedLeg && pricedLeg.bestPrice ? (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'rgba(255,255,255,0.65)' }}
                          >
                            Best {formatAmerican(pricedLeg.bestPrice.american)} ·{' '}
                            {pricedLeg.bestPrice.bookmakerTitle}
                          </Typography>
                        ) : null;
                      })()}
                    </Box>
                    <Tooltip title="Swap for the next-best leg from this game">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleSwap(index)}
                          sx={{ color: '#FFFFFF' }}
                          aria-label={`Swap leg ${index + 1}`}
                        >
                          <SwapIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 2 }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                  textAlign: 'center',
                  mb: 2
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                    COMBINED ODDS
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: oddsTextColor }}>
                    {formatAmerican(metrics.combinedAmerican)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                    TRUE CHANCE
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {formatPct(metrics.trueChance)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                    EV
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: metrics.ev >= 0 ? '#4ADE80' : '#FFFFFF' }}
                  >
                    {formatSignedPct(metrics.ev)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1 }}>
                {STAKE_PRESETS.map((preset) => (
                  <Button
                    key={preset}
                    size="small"
                    variant={stakeAmount === preset ? 'contained' : 'outlined'}
                    onClick={() => setStake(String(preset))}
                    sx={{
                      fontWeight: 700,
                      ...(stakeAmount === preset
                        ? { bgcolor: '#14532D', '&:hover': { bgcolor: '#14532D' } }
                        : { color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.4)' })
                    }}
                  >
                    ${preset}
                  </Button>
                ))}
                <TextField
                  size="small"
                  label="Stake"
                  value={stake}
                  onChange={(e) => setStake(e.target.value.replace(/[^0-9.]/g, ''))}
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  sx={{
                    width: 130,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' }
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiInputAdornment-root .MuiTypography-root': { color: 'rgba(255,255,255,0.7)' }
                  }}
                />
                <Typography variant="body2" sx={{ ml: 'auto' }}>
                  Stake {formatMoney(stakeAmount)} → Payout{' '}
                  <Box component="span" sx={{ fontWeight: 800, color: oddsTextColor }}>
                    {formatMoney(payout)}
                  </Box>
                </Typography>
              </Box>

              <Button
                onClick={handleSaveToTracker}
                disabled={isSaved || saving || stakeAmount <= 0}
                variant="contained"
                fullWidth
                startIcon={isSaved ? <CheckIcon /> : <SaveIcon />}
                sx={{
                  mt: 1.5,
                  py: 1.25,
                  fontWeight: 800,
                  bgcolor: '#F97316',
                  '&:hover': { bgcolor: '#EA580C' }
                }}
              >
                {isSaved ? 'Saved ✓' : saving ? 'Saving…' : 'Save to Bet Tracker'}
              </Button>
              {isSaved && (
                <Button
                  fullWidth
                  onClick={() => navigate('/bet-tracker')}
                  sx={{ mt: 1, color: '#FFFFFF', fontWeight: 700 }}
                >
                  View Bet Tracker
                </Button>
              )}
              {isSaved && exposureNote && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Exposure check: {exposureNote}. One bad game can sink every ticket it touches.
                </Alert>
              )}

              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}
              >
                {DISCLAIMER}
              </Typography>
            </CardContent>
          </Card>

          {/* Best Price + Best Slip (Winning Features addendum) */}
          {pricing && pricing.split && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Best price check
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Every book&apos;s price for these exact legs, compared at a $100 stake.
                  {slipChanged &&
                    ' Prices reflect the slip as built — change the sport, legs or mode to reprice a swapped slip.'}
                </Typography>

                {pricing.singleBook ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Best single book: <strong>{pricing.singleBook.bookmakerTitle}</strong> —{' '}
                    {formatAmerican(pricing.singleBook.combinedAmerican)} pays{' '}
                    <strong>{formatMoney(pricing.singleBook.payout)}</strong> on $100, all legs in
                    one place.
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    No single book carries every leg of this slip.
                  </Typography>
                )}

                {isPremium ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Split across books: {formatAmerican(pricing.split.combinedAmerican)} pays{' '}
                      <strong>{formatMoney(pricing.split.payout)}</strong> on $100
                      {pricing.difference !== null && pricing.difference > 0 && (
                        <>
                          {' '}
                          — <strong>{formatMoney(pricing.difference)} more</strong> than the best
                          single book
                        </>
                      )}
                      {pricing.difference === 0 && <> — the same as the best single book</>}.
                    </Typography>
                    <Stack spacing={0.5}>
                      {pricing.legs.map(
                        (pricedLeg) =>
                          pricedLeg.bestPrice && (
                            <Box
                              key={legKey(pricedLeg)}
                              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                            >
                              <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
                                {pricedLeg.selection}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Best {formatAmerican(pricedLeg.bestPrice.american)} at{' '}
                                {pricedLeg.bestPrice.bookmakerTitle} (
                                {pricedLeg.prices.length} book
                                {pricedLeg.prices.length === 1 ? '' : 's'})
                              </Typography>
                            </Box>
                          )
                      )}
                    </Stack>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <LockIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                      Split-slip pricing — each leg at its best book, and what the split pays
                      versus one book — is a Premium feature.
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => navigate('/subscription')}>
                      See Premium
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Round Robin converter (Winning Features addendum, Premium) */}
          {currentLegs.length >= 3 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Round Robin
                  </Typography>
                  <Chip
                    label="PREMIUM"
                    size="small"
                    sx={{ bgcolor: '#F97316', color: '#FFFFFF', fontWeight: 700 }}
                  />
                </Box>
                {isPremium ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Break this slip into every combination of the sizes you pick — each
                      combination becomes its own parlay at the unit stake, so one losing leg
                      doesn&apos;t sink the whole ticket.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          Combination sizes
                        </Typography>
                        <ToggleButtonGroup
                          value={rrSizes}
                          onChange={(_, value) => setRrSizes(value)}
                          size="small"
                        >
                          {Array.from({ length: currentLegs.length - 1 }, (_, i) => i + 2).map(
                            (size) => (
                              <ToggleButton key={size} value={size} sx={{ fontWeight: 600 }}>
                                By {size}s
                              </ToggleButton>
                            )
                          )}
                        </ToggleButtonGroup>
                        <Button
                          size="small"
                          sx={{ ml: 1, fontWeight: 700 }}
                          onClick={() =>
                            setRrSizes(
                              Array.from({ length: currentLegs.length - 1 }, (_, i) => i + 2)
                            )
                          }
                        >
                          Full round robin
                        </Button>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          Unit stake (per parlay)
                        </Typography>
                        <TextField
                          size="small"
                          value={rrUnitStake}
                          onChange={(e) =>
                            setRrUnitStake(e.target.value.replace(/[^0-9.]/g, ''))
                          }
                          inputProps={{ inputMode: 'decimal' }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                          sx={{ width: 130 }}
                        />
                      </Box>
                    </Box>

                    {roundRobin.combos.length > 0 ? (
                      <>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>{roundRobin.totalCombos}</strong> parlay
                          {roundRobin.totalCombos === 1 ? '' : 's'} (
                          {Object.keys(rrCountsBySize)
                            .sort((a, b) => Number(a) - Number(b))
                            .map((size) => `${rrCountsBySize[size]} × ${size}-leg`)
                            .join(', ')}
                          ) · total stake <strong>{formatMoney(roundRobin.totalStake)}</strong> ·
                          pays up to <strong>{formatMoney(roundRobin.maxPayout)}</strong> if every
                          leg hits.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          True chance of the full sweep: {formatPct(metrics.trueChance)} — each
                          smaller parlay hits more often than the full slip.
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={handleSaveRoundRobin}
                          disabled={rrSaving || rrSaved || rrUnitAmount <= 0}
                          startIcon={rrSaved ? <CheckIcon /> : <SaveIcon />}
                          sx={{ fontWeight: 800, bgcolor: '#F97316', '&:hover': { bgcolor: '#EA580C' } }}
                        >
                          {rrSaved
                            ? 'Saved ✓'
                            : rrSaving
                              ? 'Saving…'
                              : `Save ${roundRobin.totalCombos} parlays to Bet Tracker`}
                        </Button>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Pick at least one combination size to build the round robin.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <LockIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                      The Round Robin converter — every combination priced, saved to your Bet
                      Tracker in one tap — is a Premium feature.
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => navigate('/subscription')}>
                      See Premium
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            One leg per game, cross-game only — that keeps the true-chance math honest and the slip
            placeable at the book shown. True chance and EV are shown for every parlay, good or bad.
          </Typography>
        </>
      )}

      {builderTab === 'custom' && !gated && !loading && !subscriptionLoading && !error && data && (
        <>
          {(data.games || []).length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No games on the board
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No upcoming {sportTitle} games with odds right now — check back closer to game day.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 400px' },
                gap: 3,
                alignItems: 'start'
              }}
            >
              {/* The board: every game, every candidate leg */}
              <Stack spacing={2}>
                <TextField
                  size="small"
                  fullWidth
                  label={legView === 'props' ? 'Search teams or players…' : 'Search teams…'}
                  value={customQuery}
                  onChange={(e) => setCustomQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
                {legView === 'game' ? (visibleCustomGames.length === 0 ? (
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No games match &apos;{customQuery}&apos;
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                <>
                {visibleCustomGames.map((game) => {
                  const gameLeg = selectedLegForGame(customLegs, game.eventId);
                  return (
                    <Card key={game.eventId}>
                      <CardContent>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {game.matchup}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatStartEt(game.commenceTime)}
                          </Typography>
                          {gameLeg && (
                            <Chip
                              label={`On your slip: ${gameLeg.selection}`}
                              size="small"
                              sx={{ bgcolor: '#14532D', color: '#FFFFFF', fontWeight: 600 }}
                            />
                          )}
                        </Box>
                        {MARKET_GROUPS.map((group) => {
                          const groupCandidates = game.candidates.filter((c) => c.market === group.key);
                          if (groupCandidates.length === 0) return null;
                          return (
                            <Box key={group.key} sx={{ mt: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                              >
                                {group.label.toUpperCase()}
                              </Typography>
                              <Box sx={{ mt: 0.75 }}>
                                {groupCandidates.map((candidate) => {
                                  const selected = isCandidateSelected(customLegs, candidate);
                                  return (
                                    <Button
                                      key={`${candidate.market}-${candidate.selection}-${candidate.american}`}
                                      fullWidth
                                      variant={selected ? 'contained' : 'outlined'}
                                      onClick={() => handleToggleCandidate(candidate)}
                                      aria-pressed={selected}
                                      sx={{
                                        justifyContent: 'space-between',
                                        textTransform: 'none',
                                        mb: 0.75,
                                        py: 1,
                                        ...(selected
                                          ? { bgcolor: '#14532D', '&:hover': { bgcolor: '#14532D' } }
                                          : { color: '#FFFFFF', borderColor: 'rgba(0,0,0,0.25)' })
                                      }}
                                    >
                                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700 }}>
                                        {selected && <CheckIcon fontSize="small" />}
                                        {candidate.selection}
                                      </Box>
                                      <Box component="span" sx={{ textAlign: 'right' }}>
                                        <Box component="span" sx={{ fontWeight: 800, color: buttonOddsColor }}>
                                          {formatAmerican(candidate.american)}
                                        </Box>
                                        <Box component="span" sx={{ display: 'block', fontSize: '0.7rem', opacity: 0.75 }}>
                                          {formatPct(candidate.fairProb)} true
                                        </Box>
                                      </Box>
                                    </Button>
                                  );
                                })}
                              </Box>
                            </Box>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })}
                </>
                )) : (
                  renderPropsBoard()
                )}
              </Stack>

              {/* The hand-built slip (same card family as the suggested slip) */}
              <Box sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
                <Card sx={{ bgcolor: '#0A1F18', color: '#FFFFFF', borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Chip
                        label="BUILD YOUR OWN"
                        size="small"
                        sx={{ bgcolor: '#F97316', color: '#FFFFFF', fontWeight: 700 }}
                      />
                      {book && (
                        <Chip
                          label={`All legs at ${book.title}`}
                          size="small"
                          variant="outlined"
                          sx={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.4)' }}
                        />
                      )}
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', ml: 'auto' }}>
                        Odds as of {data.oddsAsOf}
                      </Typography>
                    </Box>

                    {customLegs.length === 0 ? (
                      <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          Your slip is empty
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5 }}>
                          Tap any price on the board to add a leg. One leg per game — you need
                          at least 2 legs to save a parlay.
                        </Typography>
                      </Box>
                    ) : (
                      <Stack divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />}>
                        {customLegs.map((leg) => (
                          <Box
                            key={`${leg.eventId}-${leg.market}-${leg.selection}`}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
                                {leg.selection}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                                {leg.matchup} · {formatStartEt(leg.commenceTime)}
                              </Typography>
                              {isPremium && Array.isArray(leg.tags) && leg.tags.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {leg.tags.map((tag) => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        color: '#FDBA74',
                                        borderColor: 'rgba(249,115,22,0.45)',
                                        bgcolor: 'rgba(249,115,22,0.15)'
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: oddsTextColor }}>
                                {formatAmerican(leg.american)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                                {formatPct(leg.fairProb)} true
                              </Typography>
                            </Box>
                            <Tooltip title="Remove leg">
                              <IconButton
                                size="small"
                                onClick={() => setCustomLegs((prev) => toggleLeg(prev, leg))}
                                sx={{ color: '#FFFFFF' }}
                                aria-label={`Remove ${leg.selection}`}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Stack>
                    )}

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 2 }} />

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1,
                        textAlign: 'center',
                        mb: 2
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                          COMBINED ODDS
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: oddsTextColor }}>
                          {customLegs.length ? formatAmerican(customMetrics.combinedAmerican) : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                          TRUE CHANCE
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {customLegs.length ? formatPct(customMetrics.trueChance) : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                          EV
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, color: customMetrics.ev >= 0 && customLegs.length ? '#4ADE80' : '#FFFFFF' }}
                        >
                          {customLegs.length ? formatSignedPct(customMetrics.ev) : '—'}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      {STAKE_PRESETS.map((preset) => (
                        <Button
                          key={preset}
                          size="small"
                          variant={customStakeAmount === preset ? 'contained' : 'outlined'}
                          onClick={() => setCustomStake(String(preset))}
                          sx={{
                            fontWeight: 700,
                            ...(customStakeAmount === preset
                              ? { bgcolor: '#14532D', '&:hover': { bgcolor: '#14532D' } }
                              : { color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.4)' })
                          }}
                        >
                          ${preset}
                        </Button>
                      ))}
                      <TextField
                        size="small"
                        label="Stake"
                        value={customStake}
                        onChange={(e) => setCustomStake(e.target.value.replace(/[^0-9.]/g, ''))}
                        inputProps={{ inputMode: 'decimal' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        sx={{
                          width: 130,
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' }
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                          '& .MuiInputAdornment-root .MuiTypography-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                      />
                      {customLegs.length > 0 && (
                        <Typography variant="body2" sx={{ ml: 'auto' }}>
                          Stake {formatMoney(customStakeAmount)} → Payout{' '}
                          <Box component="span" sx={{ fontWeight: 800, color: oddsTextColor }}>
                            {formatMoney(customPayout)}
                          </Box>
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        {customLegs.length} of {MAX_CUSTOM_LEGS} legs
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setCustomLegs([])}
                        disabled={customLegs.length === 0}
                        sx={{ color: '#FFFFFF', fontWeight: 700 }}
                      >
                        Clear
                      </Button>
                    </Box>

                    <Button
                      onClick={handleSaveCustomToTracker}
                      disabled={customIsSaved || customSaving || customStakeAmount <= 0 || !customCanSave}
                      variant="contained"
                      fullWidth
                      startIcon={customIsSaved ? <CheckIcon /> : <SaveIcon />}
                      sx={{
                        mt: 1,
                        py: 1.25,
                        fontWeight: 800,
                        bgcolor: '#F97316',
                        '&:hover': { bgcolor: '#EA580C' }
                      }}
                    >
                      {customIsSaved ? 'Saved ✓' : customSaving ? 'Saving…' : 'Save to Bet Tracker'}
                    </Button>
                    {customIsSaved && (
                      <Button
                        fullWidth
                        onClick={() => navigate('/bet-tracker')}
                        sx={{ mt: 1, color: '#FFFFFF', fontWeight: 700 }}
                      >
                        View Bet Tracker
                      </Button>
                    )}
                    {customIsSaved && customExposureNote && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Exposure check: {customExposureNote}. One bad game can sink every ticket it touches.
                      </Alert>
                    )}
                    {!customCanSave && customLegs.length === 1 && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: 'rgba(255,255,255,0.65)', textAlign: 'center' }}
                      >
                        Add 1 more leg from a different game to save this parlay.
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}
                    >
                      {DISCLAIMER}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            One leg per game — tapping another price from the same game swaps it into your slip.
            True chance and EV update as you build, with the bookmaker&apos;s margin stripped out.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ParlayBuilderScreen;
